"use client"

import { useQuery } from "@tanstack/react-query"
import { getGeneralSettings } from "@/lib/settings-api"
// Dentour 260923 — every formatter below renders "—" for a user without
// prices.view (the API has already nulled the figure; this stops it reading €0).
import { PRICE_MASK, pricesHidden } from "@/lib/price-visibility"

import {
  CURRENCY_CONFIG,
  DEFAULT_CURRENCY,
  getCurrencyDecimals,
  getCurrencySymbol,
  groupSumByCurrency,
} from "@/lib/currency-format"

// Re-exported so existing consumers can keep importing currency helpers from
// this hook; the implementations live in lib/currency-format (pure, no React)
// so server components and the public quote themes can use them too.
export { CURRENCY_CONFIG, getCurrencyDecimals, getCurrencySymbol, groupSumByCurrency }

/**
 * Hook to get the current currency setting from the application settings.
 * Uses React Query for caching and automatic refetching.
 */
export function useCurrency() {
  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: getGeneralSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 1,
  })

  const currency = (settings?.default_currency as string) || DEFAULT_CURRENCY

  return {
    currency,
    isLoading,
  }
}

/**
 * Tier 1 multi-currency: the currencies documents may be created in on this
 * clinic (config/currency.php, exposed via /settings/public). Single-currency
 * clinics get a one-item list, so callers can render static text instead of
 * a picker.
 */
export function useAllowedCurrencies() {
  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: getGeneralSettings,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  })

  const currency = (settings?.default_currency as string) || DEFAULT_CURRENCY
  const allowed = settings?.allowed_currencies as string[] | undefined
  const currencies = allowed?.length ? allowed : [currency]

  return {
    currencies,
    isMultiCurrency: currencies.length > 1,
    currency,
    isLoading,
  }
}

/**
 * Format a number as currency using the application's currency setting.
 * This is a hook that returns a formatting function.
 */
export function useFormatCurrency() {
  const { currency } = useCurrency()

  const formatCurrency = (amount: number, options?: { decimals?: boolean }) => {
    if (pricesHidden()) return PRICE_MASK
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD
    const showDecimals = options?.decimals ?? true

    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: showDecimals ? config.decimals : 0,
      maximumFractionDigits: showDecimals ? config.decimals : 0,
    }).format(amount)
  }

  const formatCurrencyNoDecimals = (amount: number) => {
    return formatCurrency(amount, { decimals: false })
  }

  const formatCurrencyWithDecimals = (amount: number) => {
    return formatCurrency(amount, { decimals: true })
  }

  return {
    formatCurrency,
    formatCurrencyNoDecimals,
    formatCurrencyWithDecimals,
    currency,
  }
}

/**
 * Standalone currency formatting function for use outside of React components.
 * Requires the currency code to be passed in.
 */
export function formatCurrencyStatic(
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  options?: { decimals?: boolean }
): string {
  if (pricesHidden()) return PRICE_MASK
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD
  const showDecimals = options?.decimals ?? true

  return new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: showDecimals ? config.decimals : 0,
    maximumFractionDigits: showDecimals ? config.decimals : 0,
  }).format(amount)
}
